import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Stack,
  ListItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Pets as PetsIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  LocalHospital as LocalHospitalIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { API_BASE_URL } from '@config/api';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '@services/dashboardService';
import { clientsService, type Client } from '@services/clientsService';
import { veterinariansService, type Veterinarian } from '@services/veterinariansService';
import { servicesService, type Service } from '@services/servicesService';
import { patientsService, type Patient } from '@services/patientsService';
import { appointmentsService } from '@services/appointmentsService';
import { visitsService } from '@services/visitsService';
import { invoicesService } from '@services/invoicesService';
import { inventoryService } from '@services/inventoryService';

// Import Dialog components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Snackbar
} from '@mui/material';

interface KPIData {
  title: string;
  value: string | number;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

interface AlertData {
  id: string;
  type: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  action?: string;
}

interface QuickAction {
  title: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning';
  onClick: () => void;
}

const ClinicDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KPIData[]>([]);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [pendingAppointments, setPendingAppointments] = useState<any[]>([]);
  const [allAppointments, setAllAppointments] = useState<any[]>([]);
  
  // Dialog states for quick actions
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const [appointmentFormLoading, setAppointmentFormLoading] = useState(false);
  const [clientsForPickers, setClientsForPickers] = useState<Client[]>([]);
  const [vetsForPickers, setVetsForPickers] = useState<Veterinarian[]>([]);
  const [servicesForPickers, setServicesForPickers] = useState<Service[]>([]);
  const [petsForClient, setPetsForClient] = useState<Patient[]>([]);

  const [patientForm, setPatientForm] = useState({
    ownerId: '',
    name: '',
    species: 'dog' as 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other',
    breed: '',
    sex: 'male' as 'male' | 'female',
    age: ''
  });

  const [visitForm, setVisitForm] = useState({
    petId: '',
    doctorId: '',
    reason: '',
    visitDate: new Date().toISOString().slice(0, 16)
  });

  const [billingForm, setBillingForm] = useState({
    clientId: '',
    amountRon: '',
    description: ''
  });

  const [inventoryForm, setInventoryForm] = useState({
    productId: '',
    batchNumber: '',
    expirationDate: '',
    quantity: '',
    unitCost: ''
  });
  const [inventoryProducts, setInventoryProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [dialogSaving, setDialogSaving] = useState(false);
  
  // Appointment form state
  const [newAppointment, setNewAppointment] = useState({
    clientId: '',
    petId: '',
    doctorId: '',
    serviceId: '',
    date: '',
    time: '',
    location: '',
    priority: 'normal',
    notes: ''
  });

  // Client form state
  const [newClient, setNewClient] = useState<{
    name: string;
    phone: string;
    email: string;
    address: string;
    gdprConsent: {
      marketing: boolean;
      sms: boolean;
      email: boolean;
    };
    notes: string;
    status: 'active' | 'inactive' | 'blocked';
  }>({
    name: '',
    phone: '',
    email: '',
    address: '',
    gdprConsent: {
      marketing: false,
      sms: false,
      email: false
    },
    notes: '',
    status: 'active'
  });

  // Incarcare date dashboard cu apeluri API reale
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.clinicId) return;
      
      setLoading(true);
      
      try {
        // Incarcam toate datele dashboard din API
        const [dashboardData, pendingResponse, allAppointmentsResponse] = await Promise.all([
          dashboardService.getAllDashboardData({
            clinicId: user.clinicId
          }),
          // Incarca programarile in asteptare confirmare
          fetch(`${API_BASE_URL}/appointments?status=pending_confirmation&clinicId=${user.clinicId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.json()),
          // Incarca TOATE programarile pentru a vedea sloturile ocupate
          fetch(`${API_BASE_URL}/appointments?clinicId=${user.clinicId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.json())
        ]);
        
        // Construim KPI-urile
        const kpis: KPIData[] = [
          {
            title: 'Programari Astazi',
            value: dashboardData.appointments.length,
            trend: 'neutral',
            icon: <EventIcon />,
            color: 'primary'
          },
          {
            title: 'Venit Astazi',
            value: `${dashboardData.revenue.today} RON`,
            trend: 'up',
            icon: <MoneyIcon />,
            color: 'success'
          },
          {
            title: 'Stoc Redus',
            value: dashboardData.stockAlerts.length,
            trend: dashboardData.stockAlerts.length > 0 ? 'down' : 'neutral',
            icon: <InventoryIcon />,
            color: dashboardData.stockAlerts.length > 0 ? 'warning' : 'primary'
          },
          {
            title: 'Vaccinari Due',
            value: dashboardData.vaccinations.length,
            trend: 'neutral',
            icon: <PetsIcon />,
            color: 'secondary'
          }
        ];
        
        setKpiData(kpis);
        
        // Construim alertele
        const alertsList: AlertData[] = [
          ...dashboardData.stockAlerts.slice(0, 3).map(alert => ({
            id: alert.id,
            type: alert.severity === 'high' ? 'error' as const : alert.severity === 'medium' ? 'warning' as const : 'info' as const,
            title: 'Stoc scazut',
            message: `${alert.itemName}: ${alert.currentStock} unitati (min: ${alert.minStock})`,
            action: 'inventory'
          })),
          ...dashboardData.vaccinations.slice(0, 2).map(vacc => ({
            id: vacc.id,
            type: vacc.isOverdue ? 'warning' as const : 'info' as const,
            title: vacc.isOverdue ? 'Vaccinare depasita' : 'Vaccinare apropiata',
            message: `${vacc.patientName} (${vacc.clientName}) - ${vacc.vaccinationType}`,
            action: 'patients'
          }))
        ];
        
        setAlerts(alertsList);
        
        // Setam programarile in asteptare
        setPendingAppointments(pendingResponse.data || []);
        
        // Setam toate programarile
        setAllAppointments(allAppointmentsResponse.data || []);
        
        // Recent activity = programarile de astazi
        setRecentActivity(dashboardData.appointments.slice(0, 5).map(app => ({
          id: app.id,
          type: 'appointment',
          title: app.service,
          description: `${app.patientName} (${app.clientName})`,
          time: app.time,
          status: app.status
        })));
        
      } catch (error) {
        console.error('Eroare la incarcarea datelor dashboard:', error);
        // In caz de eroare, afisam array-uri goale
        setKpiData([]);
        setAlerts([]);
        setRecentActivity([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.clinicId]);

  const readCsrfHeader = (): Record<string, string> => {
    const m = typeof document !== 'undefined' && document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    const t = m ? decodeURIComponent(m[1]) : '';
    return t ? { 'X-CSRF-Token': t } : {};
  };

  useEffect(() => {
    if (!appointmentDialogOpen || !user?.clinicId) return;
    let cancelled = false;
    (async () => {
      setAppointmentFormLoading(true);
      try {
        const [clRes, vetRes, svcRes] = await Promise.all([
          clientsService.getClients({ clinicId: user.clinicId, limit: 500 }),
          veterinariansService.getVeterinarians({ clinicId: user.clinicId, status: 'active' }),
          servicesService.getServices({ clinicId: user.clinicId, isActive: true })
        ]);
        if (cancelled) return;
        setClientsForPickers(clRes.clients);
        setVetsForPickers(vetRes);
        const rawSvc = svcRes as unknown as { services?: Service[]; data?: { services?: Service[] } };
        setServicesForPickers(rawSvc.services ?? rawSvc.data?.services ?? []);
      } catch {
        if (!cancelled) {
          setSnackbar({ open: true, severity: 'error', message: 'Nu s-au putut încărca clienții / medicii / serviciile.' });
        }
      } finally {
        if (!cancelled) setAppointmentFormLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentDialogOpen, user?.clinicId]);

  useEffect(() => {
    if (!newAppointment.clientId || !user?.clinicId) {
      setPetsForClient([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await patientsService.getPatients({
          clinicId: user.clinicId,
          clientId: newAppointment.clientId,
          limit: 200
        });
        if (!cancelled) setPetsForClient(res.patients);
      } catch {
        if (!cancelled) setPetsForClient([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [newAppointment.clientId, user?.clinicId]);

  useEffect(() => {
    if (!patientDialogOpen || !user?.clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const clRes = await clientsService.getClients({ clinicId: user.clinicId, limit: 500 });
        if (!cancelled) setClientsForPickers(clRes.clients);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientDialogOpen, user?.clinicId]);

  useEffect(() => {
    if (!visitDialogOpen || !user?.clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const [pRes, vRes] = await Promise.all([
          patientsService.getPatients({ clinicId: user.clinicId, limit: 300 }),
          veterinariansService.getVeterinarians({ clinicId: user.clinicId, status: 'active' })
        ]);
        if (cancelled) return;
        setPetsForClient(pRes.patients);
        setVetsForPickers(vRes);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visitDialogOpen, user?.clinicId]);

  useEffect(() => {
    if (!billingDialogOpen || !user?.clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const clRes = await clientsService.getClients({ clinicId: user.clinicId, limit: 500 });
        if (!cancelled) setClientsForPickers(clRes.clients);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingDialogOpen, user?.clinicId]);

  useEffect(() => {
    if (!inventoryDialogOpen || !user?.clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const inv = await inventoryService.getInventoryList({ clinicId: user.clinicId, limit: 500 });
        if (cancelled) return;
        const items = (inv.items || []).map((row: Record<string, unknown>) => ({
          id: String(row.id),
          name: String(row.name ?? row.code ?? row.id)
        }));
        setInventoryProducts(items);
      } catch {
        if (!cancelled) setInventoryProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inventoryDialogOpen, user?.clinicId]);

  // Functii pentru aprobare/respingere programari
  const handleApproveAppointment = async (appointmentId: string) => {
    try {
      // 1. Actualizează statusul programării
      const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...readCsrfHeader()
        },
        body: JSON.stringify({ status: 'confirmed' }) // Schimbat din 'accepted' în 'confirmed'
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Eroare la actualizare status:', response.status, errorData);
        throw new Error(`Eroare la acceptarea programarii: ${response.status} - ${errorData}`);
      }
      
      console.log('✅ Status programare actualizat la: confirmed');

      // 2. Găsește datele programării pentru notificare
      const appointment = pendingAppointments.find(apt => apt.id === appointmentId);
      if (appointment) {
        // 3. Trimite notificare către client
        try {
          await fetch(`${API_BASE_URL}/notifications`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...readCsrfHeader()
            },
            body: JSON.stringify({
              userId: appointment.ownerId,        // Backend cere 'userId' nu 'recipientId'
              type: 'appointment',
              title: '✅ Programare confirmată',
              message: `Programarea dumneavoastră pentru ${appointment.animalName} din data de ${new Date(appointment.scheduledAt).toLocaleDateString('ro-RO', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })} a fost confirmată de către clinică.`,
              channels: ['email', 'inApp'],      // Backend cere 'channels' obligatoriu
              data: {
                appointmentId: appointment.id,
                animalName: appointment.animalName,
                scheduledAt: appointment.scheduledAt,
                clinicName: appointment.clinicName || 'Clinica noastră'
              }
            })
          });
          console.log('✅ Notificare trimisă către client');
        } catch (notifError) {
          console.error('⚠️ Eroare la trimiterea notificării:', notifError);
          // Nu blocăm procesul dacă notificarea eșuează
        }
      }

      // 4. Reload dashboard data
      setPendingAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
      
      // 5. Afișează mesaj de succes
      alert('Programare confirmată cu succes! Clientul a fost notificat.');
    } catch (error) {
      console.error('Error approving appointment:', error);
      alert('Eroare la acceptarea programarii');
    }
  };

  const handleRejectAppointment = async (appointmentId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...readCsrfHeader()
        },
        body: JSON.stringify({ status: 'rejected' })
      });

      if (!response.ok) {
        throw new Error('Eroare la respingerea programarii');
      }

      // Reload dashboard data
      setPendingAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
      
      // Afiseaza mesaj de succes
      alert('Programare respinsa!');
    } catch (error) {
      console.error('Error rejecting appointment:', error);
      alert('Eroare la respingerea programarii');
    }
  };

  const quickActions: QuickAction[] = [
    {
      title: 'Adauga Programare',
      icon: <AddIcon />,
      color: 'primary',
      onClick: () => setAppointmentDialogOpen(true)
    },
    {
      title: 'Client Nou',
      icon: <PeopleIcon />,
      color: 'secondary',
      onClick: () => setClientDialogOpen(true)
    },
    {
      title: 'Pacient Nou',
      icon: <PetsIcon />,
      color: 'success',
      onClick: () => setPatientDialogOpen(true)
    },
    {
      title: 'Vizita Medicala',
      icon: <LocalHospitalIcon />,
      color: 'warning',
      onClick: () => setVisitDialogOpen(true)
    },
    {
      title: 'Chitanta',
      icon: <MoneyIcon />,
      color: 'primary',
      onClick: () => setBillingDialogOpen(true)
    },
    {
      title: 'Receptie Stoc',
      icon: <InventoryIcon />,
      color: 'secondary',
      onClick: () => setInventoryDialogOpen(true)
    }
  ];

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon color="success" />;
      case 'down':
        return <TrendingDownIcon color="error" />;
      default:
        return null;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'appointment':
        return <EventIcon color="primary" />;
      case 'payment':
        return <MoneyIcon color="success" />;
      case 'visit':
        return <LocalHospitalIcon color="secondary" />;
      case 'prescription':
        return <AssessmentIcon color="warning" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const handleAlertAction = (alertId: string, action: string) => {
    switch (alertId) {
      case '1': // Stoc Minim
        navigate('/clinic/inventory');
        break;
      case '2': // Aprobare In Asteptare
        navigate('/clinic/prescriptions');
        break;
      case '3': // Sold Restant
        navigate('/clinic/billing');
        break;
      default:
        console.log(`Action: ${action} for alert: ${alertId}`);
    }
  };

  const handleCloseAppointmentDialog = () => {
    setAppointmentDialogOpen(false);
    setNewAppointment({
      clientId: '',
      petId: '',
      doctorId: '',
      serviceId: '',
      date: '',
      time: '',
      location: '',
      priority: 'normal',
      notes: ''
    });
  };

  const handleSaveAppointment = async () => {
    if (!user?.clinicId) return;
    const { clientId, petId, doctorId, serviceId, date, time, notes, priority } = newAppointment;
    if (!clientId || !petId || !doctorId || !date || !time) {
      setSnackbar({ open: true, severity: 'error', message: 'Completați client, animal, medic, data și ora.' });
      return;
    }
    const startLocal = new Date(`${date}T${time.length === 5 ? `${time}:00` : time}`);
    if (Number.isNaN(startLocal.getTime())) {
      setSnackbar({ open: true, severity: 'error', message: 'Data sau ora nu sunt valide.' });
      return;
    }
    const mins = startLocal.getMinutes();
    if (mins !== 0 && mins !== 30) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Ora trebuie să fie la :00 sau :30 (cerință sistem programări).'
      });
      return;
    }
    if (startLocal.getTime() <= Date.now()) {
      setSnackbar({ open: true, severity: 'error', message: 'Programarea trebuie să fie în viitor.' });
      return;
    }
    const svc = servicesForPickers.find((s) => s.id === serviceId);
    const durationMinutes = svc?.duration ?? 30;
    const endLocal = new Date(startLocal.getTime() + durationMinutes * 60_000);
    setDialogSaving(true);
    try {
      await appointmentsService.createAppointment({
        clinicId: user.clinicId,
        clientId,
        patientId: petId,
        doctorId,
        serviceId: serviceId || undefined,
        start: startLocal.toISOString(),
        end: endLocal.toISOString(),
        notes: notes || undefined,
        durationMinutes,
        emergency: priority === 'urgent',
        reason: svc?.name || notes || 'Programare din dashboard clinică'
      });
      setSnackbar({ open: true, severity: 'success', message: 'Programarea a fost creată și trimisă spre confirmare.' });
      handleCloseAppointmentDialog();
      window.location.reload();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Eroare la crearea programării';
      setSnackbar({ open: true, severity: 'error', message: String(msg) });
    } finally {
      setDialogSaving(false);
    }
  };

  const handleCloseClientDialog = () => {
    setClientDialogOpen(false);
    setNewClient({
      name: '',
      phone: '',
      email: '',
      address: '',
      gdprConsent: {
        marketing: false,
        sms: false,
        email: false
      },
      notes: '',
      status: 'active'
    });
  };

  const handleSaveClient = async () => {
    const full = newClient.name.trim();
    if (!full || !newClient.phone.trim()) {
      setSnackbar({ open: true, severity: 'error', message: 'Numele și telefonul sunt obligatorii.' });
      return;
    }
    const email = newClient.email.trim();
    if (!email) {
      setSnackbar({ open: true, severity: 'error', message: 'Emailul este obligatoriu pentru crearea contului de client.' });
      return;
    }
    const parts = full.split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || parts[0];
    setDialogSaving(true);
    try {
      await clientsService.createClient({
        firstName,
        lastName,
        email,
        phone: newClient.phone.trim(),
        address: newClient.address
          ? {
              street: newClient.address,
              city: '',
              county: '',
              postalCode: '',
              country: 'Romania'
            }
          : undefined,
        tags: [],
        gdprConsent: newClient.gdprConsent.sms || newClient.gdprConsent.email || newClient.gdprConsent.marketing,
        communicationPreferences: {
          sms: newClient.gdprConsent.sms,
          email: newClient.gdprConsent.email,
          whatsapp: false
        }
      });
      setSnackbar({ open: true, severity: 'success', message: 'Client salvat în sistem.' });
      handleCloseClientDialog();
      window.location.reload();
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.message || 'Eroare la salvarea clientului.' });
    } finally {
      setDialogSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca dashboard-ul...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Dashboard Clinica
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Buna ziua, {user?.firstName || 'Admin'}! Iata o privire de ansamblu asupra activitatii clinicii.
          </Typography>
        </Box>
        <Tooltip title="Actualizeaza datele">
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Programari in asteptare confirmare */}
      {!loading && pendingAppointments.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'info.light',
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              🔔 Programari noi in asteptare confirmare ({pendingAppointments.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Urmatoarele programari necesita confirmarea ta:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pendingAppointments.map((apt: any) => (
                <Card 
                  key={apt.id}
                  sx={{ 
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600} gutterBottom>
                          {apt.animalName || 'Animal necunoscut'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          👤 Proprietar: {apt.ownerFirstName} {apt.ownerLastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          👨‍⚕️ Veterinar: Dr. {apt.veterinarianFirstName} {apt.veterinarianLastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          📅 {new Date(apt.scheduledAt).toLocaleDateString('ro-RO', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📝 Motiv: {apt.reason || 'Nespecificat'}
                        </Typography>
                        {apt.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            💬 Note: {apt.notes}
                          </Typography>
                        )}
                        {apt.emergency && (
                          <Chip 
                            label="🚨 URGENTA" 
                            color="error" 
                            size="small" 
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApproveAppointment(apt.id)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          ✓ Accepta
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRejectAppointment(apt.id)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          ✗ Respinge
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Toate programarile viitoare */}
      {!loading && allAppointments.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                📅 Programari Viitoare ({allAppointments.filter(apt => new Date(apt.scheduledAt) >= new Date() && (apt.status === 'confirmed' || apt.status === 'scheduled')).length})
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/clinic/appointments')}
                sx={{ textTransform: 'none' }}
              >
                Vezi toate programarile
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflowY: 'auto' }}>
              {allAppointments
                .filter(apt => new Date(apt.scheduledAt) >= new Date() && (apt.status === 'confirmed' || apt.status === 'scheduled'))
                .slice(0, 10)
                .map((apt: any) => (
                  <Box
                    key={apt.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                        borderColor: 'primary.main'
                      },
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/clinic/appointments')}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {apt.animalName || 'Animal necunoscut'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          👤 {apt.ownerFirstName} {apt.ownerLastName} • 
                          👨‍⚕️ Dr. {apt.veterinarianFirstName} {apt.veterinarianLastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📅 {new Date(apt.scheduledAt).toLocaleDateString('ro-RO', { 
                            weekday: 'short',
                            day: 'numeric', 
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })} • 📝 {apt.reason || 'Consultatie'}
                        </Typography>
                      </Box>
                      <Chip
                        label={apt.status === 'confirmed' ? '✓ Confirmata' : apt.status === 'scheduled' ? 'Programata' : apt.status}
                        color={apt.status === 'confirmed' ? 'success' : 'info'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                ))}
              {allAppointments.filter(apt => new Date(apt.scheduledAt) >= new Date() && (apt.status === 'confirmed' || apt.status === 'scheduled')).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nu exista programari viitoare confirmate
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}


      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {kpiData.length > 0 ? (
          kpiData.map((kpi, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={`kpi-${kpi.title}-${index}`}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {kpi.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={600} color={`${kpi.color}.main`}>
                        {kpi.value}
                      </Typography>
                      {kpi.change && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          {getTrendIcon(kpi.trend)}
                          <Typography 
                            variant="body2" 
                            color={kpi.trend === 'up' ? 'success.main' : 'error.main'}
                            sx={{ ml: 0.5 }}
                          >
                            {kpi.change > 0 ? '+' : ''}{kpi.change}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Avatar sx={{ bgcolor: `${kpi.color}.light`, color: `${kpi.color}.main` }}>
                      {kpi.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid size={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  📊 Nu exista date KPI disponibile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Datele vor fi afisate aici odata ce vor fi disponibile din sistem
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Actiuni Rapide
              </Typography>
              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={`action-${action.title}-${index}`}>
                    <Button
                      variant="outlined"
                      color={action.color}
                      startIcon={action.icon}
                      onClick={action.onClick}
                      sx={{ 
                        width: '100%', 
                        height: 60,
                        flexDirection: 'column',
                        gap: 0.5
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {action.title}
                      </Typography>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Alerte & Notificari
              </Typography>
              <Stack spacing={2}>
                {alerts.length > 0 ? (
                  alerts.map((alert) => (
                    <Alert 
                      key={alert.id}
                      severity={alert.type}
                      action={
                        alert.action && (
                          <Button 
                            color="inherit" 
                            size="small"
                            onClick={() => handleAlertAction(alert.id, alert.action || '')}
                          >
                            {alert.action}
                          </Button>
                        )
                      }
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {alert.title}
                      </Typography>
                      <Typography variant="body2">
                        {alert.message}
                      </Typography>
                    </Alert>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      🔔 Nu exista alerte
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toate notificarile sunt la zi
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Activitate Recenta
              </Typography>
              {recentActivity.length > 0 ? (
                <List>
                  {recentActivity.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      <ListItem>
                        <ListItemIcon>
                          {getActivityIcon(activity.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.message}
                          secondary={activity.time}
                        />
                      </ListItem>
                      {index < recentActivity.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    📋 Nu exista activitate recenta
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activitatile vor fi afisate aici pe masura ce apar
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Action Dialogs */}
      
      {/* Create Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onClose={handleCloseAppointmentDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Programare Noua
        </DialogTitle>
        <DialogContent>
          {appointmentFormLoading && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Client Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={newAppointment.clientId || ''}
                  onChange={(e) => setNewAppointment({ ...newAppointment, clientId: e.target.value, petId: '' })}
                  label="Client"
                >
                  {clientsForPickers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.firstName} {c.lastName} — {c.phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Pet Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth disabled={!newAppointment.clientId}>
                <InputLabel>Animal</InputLabel>
                <Select
                  value={newAppointment.petId || ''}
                  onChange={(e) => setNewAppointment({ ...newAppointment, petId: e.target.value })}
                  label="Animal"
                >
                  {petsForClient.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} ({p.species})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Doctor Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Doctor</InputLabel>
                <Select
                  value={newAppointment.doctorId || ''}
                  onChange={(e) => setNewAppointment({ ...newAppointment, doctorId: e.target.value })}
                  label="Doctor"
                >
                  {vetsForPickers.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      Dr. {v.firstName} {v.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Service Selection */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Serviciu</InputLabel>
                <Select
                  value={newAppointment.serviceId || ''}
                  onChange={(e) => setNewAppointment({ ...newAppointment, serviceId: e.target.value })}
                  label="Serviciu"
                >
                  <MenuItem value="">
                    <em>Fără serviciu (durată 30 min)</em>
                  </MenuItem>
                  {servicesForPickers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} ({s.duration} min)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date and Time */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data"
                type="date"
                value={newAppointment.date || ''}
                onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ora"
                type="time"
                value={newAppointment.time || ''}
                onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Location */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Locatie</InputLabel>
                <Select
                  value={newAppointment.location || ''}
                  onChange={(e) => setNewAppointment({...newAppointment, location: e.target.value})}
                  label="Locatie"
                >
                  <MenuItem value="sediu_principal">Sediu Principal</MenuItem>
                  <MenuItem value="sucursala_1">Sucursala 1</MenuItem>
                  <MenuItem value="sucursala_2">Sucursala 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Priority */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Prioritate</InputLabel>
                <Select
                  value={newAppointment.priority || 'normal'}
                  onChange={(e) => setNewAppointment({...newAppointment, priority: e.target.value})}
                  label="Prioritate"
                >
                  <MenuItem value="low">Scazuta</MenuItem>
                  <MenuItem value="normal">Normala</MenuItem>
                  <MenuItem value="high">Ridicata</MenuItem>
                  <MenuItem value="urgent">Urgenta</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Observatii"
                multiline
                rows={3}
                value={newAppointment.notes || ''}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                placeholder="Observatii suplimentare despre programare..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAppointmentDialog} color="inherit" disabled={dialogSaving}>
            Anuleaza
          </Button>
          <Button onClick={handleSaveAppointment} variant="contained" color="primary" disabled={dialogSaving}>
            {dialogSaving ? 'Se salvează…' : 'Creeaza Programarea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={clientDialogOpen} onClose={handleCloseClientDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Client Nou
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Nume complet */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Nume complet"
                value={newClient.name}
                onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                required
              />
            </Grid>

            {/* Telefon si Email */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefon"
                value={newClient.phone}
                onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
              />
            </Grid>

            {/* Adresa */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Adresa"
                multiline
                rows={2}
                value={newClient.address}
                onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                placeholder="Strada, numarul, orasul, judetul..."
              />
            </Grid>

            {/* Consimtamant GDPR */}
            <Grid size={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Consimtamant GDPR
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.sms}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, sms: e.target.checked}
                      })}
                    />
                  }
                  label="SMS - pentru notificari despre programari si servicii"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.email}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, email: e.target.checked}
                      })}
                    />
                  }
                  label="Email - pentru comunicari si rapoarte"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newClient.gdprConsent.marketing}
                      onChange={(e) => setNewClient({
                        ...newClient,
                        gdprConsent: {...newClient.gdprConsent, marketing: e.target.checked}
                      })}
                    />
                  }
                  label="Marketing - pentru oferte si promotii"
                />
              </FormGroup>
            </Grid>

            {/* Status */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newClient.status}
                  onChange={(e) => setNewClient({...newClient, status: e.target.value as 'active' | 'inactive' | 'blocked'})}
                  label="Status"
                >
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                  <MenuItem value="blocked">Blocat</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Observatii */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Observatii"
                multiline
                rows={3}
                value={newClient.notes}
                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                placeholder="Observatii suplimentare despre client..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseClientDialog} color="inherit" disabled={dialogSaving}>
            Anuleaza
          </Button>
          <Button onClick={handleSaveClient} variant="contained" color="primary" disabled={dialogSaving}>
            {dialogSaving ? 'Se salvează…' : 'Salveaza Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Patient Dialog */}
      <Dialog
        open={patientDialogOpen}
        onClose={() => {
          setPatientDialogOpen(false);
          setPatientForm({
            ownerId: '',
            name: '',
            species: 'dog',
            breed: '',
            sex: 'male',
            age: ''
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Pacient Nou
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Proprietar (client)</InputLabel>
                  <Select
                    value={patientForm.ownerId}
                    label="Proprietar (client)"
                    onChange={(e) => setPatientForm({ ...patientForm, ownerId: e.target.value })}
                  >
                    {clientsForPickers.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Nume animal"
                  value={patientForm.name}
                  onChange={(e) => setPatientForm({ ...patientForm, name: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Specie</InputLabel>
                  <Select
                    value={patientForm.species}
                    label="Specie"
                    onChange={(e) =>
                      setPatientForm({
                        ...patientForm,
                        species: e.target.value as typeof patientForm.species
                      })
                    }
                  >
                    <MenuItem value="dog">Caine</MenuItem>
                    <MenuItem value="cat">Pisica</MenuItem>
                    <MenuItem value="bird">Pasare</MenuItem>
                    <MenuItem value="rabbit">Iepure</MenuItem>
                    <MenuItem value="horse">Cal</MenuItem>
                    <MenuItem value="other">Alta</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Rasa"
                  value={patientForm.breed}
                  onChange={(e) => setPatientForm({ ...patientForm, breed: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Vârstă (ani, opțional)"
                  type="number"
                  value={patientForm.age}
                  onChange={(e) => setPatientForm({ ...patientForm, age: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Sex</InputLabel>
                  <Select
                    value={patientForm.sex}
                    label="Sex"
                    onChange={(e) =>
                      setPatientForm({ ...patientForm, sex: e.target.value as 'male' | 'female' })
                    }
                  >
                    <MenuItem value="male">Mascul</MenuItem>
                    <MenuItem value="female">Femela</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setPatientDialogOpen(false);
              setPatientForm({
                ownerId: '',
                name: '',
                species: 'dog',
                breed: '',
                sex: 'male',
                age: ''
              });
            }}
            color="inherit"
            disabled={dialogSaving}
          >
            Anuleaza
          </Button>
          <Button
            variant="contained"
            disabled={dialogSaving}
            onClick={async () => {
              if (!patientForm.ownerId || !patientForm.name.trim()) {
                setSnackbar({ open: true, severity: 'error', message: 'Proprietarul și numele animalului sunt obligatorii.' });
                return;
              }
              const ageNum = parseInt(patientForm.age, 10);
              setDialogSaving(true);
              try {
                const created = await patientsService.createPatient({
                  name: patientForm.name.trim(),
                  species: patientForm.species,
                  breed: patientForm.breed.trim() || 'Necunoscută',
                  sex: patientForm.sex,
                  age: Number.isFinite(ageNum) ? ageNum : 0,
                  ownerId: patientForm.ownerId,
                  tags: [],
                  allergies: []
                });
                if (!created) {
                  throw new Error('Răspuns neașteptat de la server');
                }
                setSnackbar({ open: true, severity: 'success', message: 'Pacient înregistrat.' });
                setPatientDialogOpen(false);
                setPatientForm({
                  ownerId: '',
                  name: '',
                  species: 'dog',
                  breed: '',
                  sex: 'male',
                  age: ''
                });
                window.location.reload();
              } catch (err: any) {
                const msg = err?.response?.data?.error || err?.message || 'Eroare la salvarea pacientului.';
                setSnackbar({ open: true, severity: 'error', message: String(msg) });
              } finally {
                setDialogSaving(false);
              }
            }}
          >
            {dialogSaving ? 'Se salvează…' : 'Salveaza'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Visit Dialog */}
      <Dialog
        open={visitDialogOpen}
        onClose={() => {
          setVisitDialogOpen(false);
          setVisitForm({
            petId: '',
            doctorId: '',
            reason: '',
            visitDate: new Date().toISOString().slice(0, 16)
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Vizita Medicala Noua
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Pacient</InputLabel>
                  <Select
                    value={visitForm.petId}
                    label="Pacient"
                    onChange={(e) => setVisitForm({ ...visitForm, petId: e.target.value })}
                  >
                    {petsForClient.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} — {p.clientName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Doctor</InputLabel>
                  <Select
                    value={visitForm.doctorId}
                    label="Doctor"
                    onChange={(e) => setVisitForm({ ...visitForm, doctorId: e.target.value })}
                  >
                    {vetsForPickers.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        Dr. {v.firstName} {v.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Data și ora consultației"
                  type="datetime-local"
                  value={visitForm.visitDate}
                  onChange={(e) => setVisitForm({ ...visitForm, visitDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Motivul vizitei"
                  multiline
                  rows={3}
                  value={visitForm.reason}
                  onChange={(e) => setVisitForm({ ...visitForm, reason: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setVisitDialogOpen(false);
              setVisitForm({
                petId: '',
                doctorId: '',
                reason: '',
                visitDate: new Date().toISOString().slice(0, 16)
              });
            }}
            color="inherit"
            disabled={dialogSaving}
          >
            Anuleaza
          </Button>
          <Button
            variant="contained"
            disabled={dialogSaving || !user?.clinicId}
            onClick={async () => {
              if (!user?.clinicId || !visitForm.petId || !visitForm.doctorId) {
                setSnackbar({ open: true, severity: 'error', message: 'Alegeți pacientul și medicul.' });
                return;
              }
              const pet = petsForClient.find((p) => p.id === visitForm.petId);
              if (!pet) {
                setSnackbar({ open: true, severity: 'error', message: 'Pacient invalid.' });
                return;
              }
              setDialogSaving(true);
              try {
                const visitDateIso = new Date(visitForm.visitDate).toISOString();
                await visitsService.createVisit({
                  clinicId: user.clinicId,
                  petId: visitForm.petId,
                  ownerId: pet.clientId,
                  doctorId: visitForm.doctorId,
                  reason: visitForm.reason.trim() || 'Consultație',
                  visitDate: visitDateIso,
                  visitType: 'consultation'
                });
                setSnackbar({ open: true, severity: 'success', message: 'Consultație creată.' });
                setVisitDialogOpen(false);
                setVisitForm({
                  petId: '',
                  doctorId: '',
                  reason: '',
                  visitDate: new Date().toISOString().slice(0, 16)
                });
                window.location.reload();
              } catch (err: any) {
                const msg =
                  err?.response?.data?.message ||
                  err?.response?.data?.error ||
                  err?.message ||
                  'Eroare la crearea consultației.';
                setSnackbar({ open: true, severity: 'error', message: String(msg) });
              } finally {
                setDialogSaving(false);
              }
            }}
          >
            {dialogSaving ? 'Se salvează…' : 'Salveaza'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog
        open={billingDialogOpen}
        onClose={() => {
          setBillingDialogOpen(false);
          setBillingForm({ clientId: '', amountRon: '', description: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Chitanta Noua
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={billingForm.clientId}
                    label="Client"
                    onChange={(e) => setBillingForm({ ...billingForm, clientId: e.target.value })}
                  >
                    {clientsForPickers.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Suma (RON)"
                  type="number"
                  value={billingForm.amountRon}
                  onChange={(e) => setBillingForm({ ...billingForm, amountRon: e.target.value })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Descriere / servicii"
                  multiline
                  rows={2}
                  value={billingForm.description}
                  onChange={(e) => setBillingForm({ ...billingForm, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setBillingDialogOpen(false);
              setBillingForm({ clientId: '', amountRon: '', description: '' });
            }}
            color="inherit"
            disabled={dialogSaving}
          >
            Anuleaza
          </Button>
          <Button
            variant="contained"
            disabled={dialogSaving}
            onClick={async () => {
              const amount = parseFloat(billingForm.amountRon);
              if (!billingForm.clientId || !Number.isFinite(amount) || amount <= 0) {
                setSnackbar({ open: true, severity: 'error', message: 'Client și sumă validă (RON) sunt obligatorii.' });
                return;
              }
              const cents = Math.round(amount * 100);
              setDialogSaving(true);
              try {
                await invoicesService.create({
                  client_id: billingForm.clientId,
                  items: [
                    {
                      description: billingForm.description.trim() || 'Servicii clinice',
                      quantity: 1,
                      unit_price_cents: cents
                    }
                  ],
                  notes: billingForm.description.trim() || undefined
                });
                setSnackbar({ open: true, severity: 'success', message: 'Factură ciornă creată.' });
                setBillingDialogOpen(false);
                setBillingForm({ clientId: '', amountRon: '', description: '' });
                window.location.reload();
              } catch (err: any) {
                const msg = err?.response?.data?.error || err?.message || 'Eroare la crearea facturii.';
                setSnackbar({ open: true, severity: 'error', message: String(msg) });
              } finally {
                setDialogSaving(false);
              }
            }}
          >
            {dialogSaving ? 'Se salvează…' : 'Salveaza'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Inventory Receipt Dialog */}
      <Dialog
        open={inventoryDialogOpen}
        onClose={() => {
          setInventoryDialogOpen(false);
          setInventoryForm({ productId: '', batchNumber: '', expirationDate: '', quantity: '', unitCost: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Receptie Stoc Noua
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Produs</InputLabel>
                  <Select
                    value={inventoryForm.productId}
                    label="Produs"
                    onChange={(e) => setInventoryForm({ ...inventoryForm, productId: e.target.value })}
                  >
                    {inventoryProducts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Număr lot"
                  value={inventoryForm.batchNumber}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, batchNumber: e.target.value })}
                  placeholder="ex. LOT-2026-001"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Data expirării"
                  type="date"
                  value={inventoryForm.expirationDate}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, expirationDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Cantitate"
                  type="number"
                  value={inventoryForm.quantity}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Preț unitar"
                  type="number"
                  value={inventoryForm.unitCost}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, unitCost: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setInventoryDialogOpen(false);
              setInventoryForm({ productId: '', batchNumber: '', expirationDate: '', quantity: '', unitCost: '' });
            }}
            color="inherit"
            disabled={dialogSaving}
          >
            Anuleaza
          </Button>
          <Button
            variant="contained"
            disabled={dialogSaving}
            onClick={async () => {
              const qty = parseFloat(inventoryForm.quantity);
              const cost = parseFloat(inventoryForm.unitCost);
              if (
                !inventoryForm.productId ||
                !inventoryForm.batchNumber.trim() ||
                !inventoryForm.expirationDate ||
                !Number.isFinite(qty) ||
                qty <= 0 ||
                !Number.isFinite(cost) ||
                cost < 0
              ) {
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: 'Completați produsul, lotul, expirarea, cantitatea și prețul.'
                });
                return;
              }
              setDialogSaving(true);
              try {
                await inventoryService.createBatch({
                  productId: inventoryForm.productId,
                  batchNumber: inventoryForm.batchNumber.trim(),
                  expirationDate: inventoryForm.expirationDate,
                  quantity: qty,
                  unitCost: cost
                });
                setSnackbar({ open: true, severity: 'success', message: 'Recepție stoc înregistrată.' });
                setInventoryDialogOpen(false);
                setInventoryForm({ productId: '', batchNumber: '', expirationDate: '', quantity: '', unitCost: '' });
                window.location.reload();
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Eroare la recepția de stoc.';
                setSnackbar({ open: true, severity: 'error', message: String(msg) });
              } finally {
                setDialogSaving(false);
              }
            }}
          >
            {dialogSaving ? 'Se salvează…' : 'Salveaza'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClinicDashboard;
